import { useEffect, useState, useMemo, useRef } from 'react';
import { Button, Col, DatePicker, Form, Input, Row, Select, Space, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import fetchApi from '../../../utils/api/fetchApi';
import system from '../../../config/systematic';
import antMessage from '../../../utils/extend/AntdNotification';
import dayjs from 'dayjs';
import { useReactToPrint } from 'react-to-print';
import getList from '../../universal/getList';
import DownloadExcel from '../../report/DownloadExcel';

const { RangePicker } = DatePicker;

// ================= 你的原数据类型，完全没改 =================
interface QuoteItem {
    productId: number;
    productName: string;
    productUnitName: string;
    selfId: string | null;
    vendorId: string;
    vendorName: string;
    univalence: number;
    bNum?: number;
    totalPrice?: number;
    submissionTime: string;
}

interface TableRowData {
    key: string;
    productName: string;
    productUnitName: string;
    header: string,
    selfId: string | null;
    [vendorKey: string]: any;
}

export default function QuotationComparisonTwo() {
    const wmService: any = system.wmService;

    const [initRows, setinitRows] = useState<any>([]);
    const [rows, setRows] = useState<any>([]);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [form] = Form.useForm();
    const [quotationNotice, setQuotationNotice] = useState<any>();
    const [sortState, setSortState] = useState<{
        field: string | null;
        order: 'ascend' | 'descend' | null;
    }>({
        field: null,
        order: null,
    });

    const contentRef = useRef<HTMLDivElement>(null);
    // ✅ 新增：动态计算打印缩放比例，确保缩到一张纸
    const [printScale, setPrintScale] = useState(1);

    // ================= 你的原数据处理逻辑，完全没改 =================
    const vendors = useMemo(() => {
        const vendorMap = new Map<string, { vendorId: string; vendorName: string }>();
        initRows.forEach((item: any) => {
            const compositeKey = `${item.vendorId}_${item.vendorName}`;
            if (!vendorMap.has(compositeKey)) {
                vendorMap.set(compositeKey, {
                    vendorId: item.vendorId,
                    vendorName: item.vendorName,
                });
            }
        });
        return Array.from(vendorMap.values());
    }, [initRows]);


    useEffect(() => {

        const fetchData = async () => {
            fetchApi.GET(wmService,
                {
                    apiId: 'quotationNotice',
                    apiExtend: 'showList'
                }).then(res => {
                    if (res.success) {

                        setQuotationNotice(res.data);
                    }
                });

        };
        fetchData();
    }, []);

    // ================= 屏幕显示用的列，完全保留你的逻辑 =================
    const screenColumns: ColumnsType<TableRowData> = useMemo(() => {
        const fixedColumns: Array<any> = [
            { title: '产品', dataIndex: 'productName', key: 'productName', width: 15, header: "产品" },
            { title: '单位', dataIndex: 'productUnitName', key: 'productUnitName', width: 10, header: "单位" },
            { title: '进货价', dataIndex: 'oldPrice', key: 'oldPrice', width: 15, header: "进货价" },
        ];

        // 核心修改点：使用 Map 对 vendors 进行去重，以 vendorId 为 Key
        // Map 会自动保留第一次出现的值，后续重复的 vendorId 会被覆盖（实际上是忽略）
        const uniqueVendorsMap = new Map<number, typeof vendors[0]>();

        vendors.forEach((vendor: any) => {
            // 如果 Map 中不存在该 vendorId，则添加
            if (!uniqueVendorsMap.has(vendor.vendorId)) {
                uniqueVendorsMap.set(vendor.vendorId, vendor);
            }
        });

        // 将 Map 的值转换回数组，用于生成 columns
        const uniqueVendors = Array.from(uniqueVendorsMap.values());

        const vendorColumns: ColumnsType<TableRowData> = uniqueVendors.map(vendor => {
            const vendorColumnKey = `${vendor.vendorId}`;
            return {
                title: vendor.vendorName,
                dataIndex: vendorColumnKey,
                key: vendorColumnKey,
                header: vendor.vendorName,
                width: 15,
                align: 'center',
                sorter: (a: TableRowData, b: TableRowData) => {
                    const aVal = a[vendorColumnKey];
                    const bVal = b[vendorColumnKey];
                    if (aVal == null && bVal == null) return 0;
                    if (aVal == null) return 1;
                    if (bVal == null) return -1;
                    return aVal - bVal;
                },
                sortOrder: sortState.field === vendorColumnKey ? sortState.order : null,
                render: (value: number | undefined) => value == null ? '-' : value.toFixed(2),
            };
        });

        return [...fixedColumns, ...vendorColumns];
    }, [vendors, sortState]);

    // ================= 打印专用列，去掉fixed避免冲突 =================
    const printColumns: ColumnsType<TableRowData> = useMemo(() => {
        return screenColumns.map(col => ({
            ...col,
            fixed: undefined, // 打印必须去掉固定列
            align: col.align || 'center',
        }));
    }, [screenColumns]);

    // ================= 你的原排序/初始化/接口逻辑，完全没改 =================
    const handleTableChange = (
        pagination: any,
        filters: any,
        sorter: SorterResult<TableRowData> | SorterResult<TableRowData>[]
    ) => {
        if (!Array.isArray(sorter)) {
            const { column, order } = sorter;
            setSortState({
                field: column?.key as string || null,
                order: order || null,
            });
        }
    };

    useEffect(() => {
        form.setFieldValue('submissionTime', [
            dayjs().subtract(1, 'month').startOf('day'),
            dayjs().endOf('day'),
        ]);
    }, [form]);

    const onFinish = async () => {
        setIsLoading(true);

        try {
            const values = await form.validateFields();
            const formattedValues = {
                ...values,
                submissionTime: values.submissionTime
                    ? [values.submissionTime[0].format('YYYY-MM-DD'), values.submissionTime[1].format('YYYY-MM-DD')]
                    : undefined,
                mountDataOptions: {
                    userMount: {
                        isDept: true
                    }
                }

            };

            // 1. 先获取主数据
            const res = await fetchApi.GET(wmService, {
                apiId: 'quotationOrder',
                apiExtend: 'quotationProductComparisonEx',
                apiData: formattedValues,
            });

            if (!res.success) {
                antMessage('error', '获取数据失败', res.message || '未知错误');
                setIsLoading(false);
                return;
            }
            setinitRows(res.data)
            // 2. 构建基础数据Map
            const dataMap = new Map<string, any>();
            res.data.forEach((item: any) => {

                const key = `${item.productName}-${item.productUnitName}`;
                if (!dataMap.has(key)) {
                    dataMap.set(key, {
                        key,
                        productName: item.productName,
                        productUnitName: item.productUnitName,
                        selfId: item.selfId,
                        oldPrice: 0, // 先初始化为0
                    });
                }
                const row = dataMap.get(key)!;
                const vendorColumnKey = `${item.vendorId}`;
                row[vendorColumnKey] = item.univalence;
            });

            // 3. 转换为数组
            let rows = Array.from(dataMap.values());

            // 4. 同步获取每个商品的 oldPrice（关键修改）
            for (let i = 0; i < rows.length; i++) {
                const row = rows[i];

                try {
                    const oldPriceRes = await fetchApi.GET(wmService, {
                        apiId: 'quotationOrder',
                        apiExtend: 'quotationProductComparisonExEx',
                        apiData: {
                            productUnitName: row.productUnitName,
                            productName: row.productName,
                            submissionTime: formattedValues.submissionTime,
                            mountDataOptions: {
                                userMount: {
                                    isDept: true
                                }
                            }
                        },
                    });


                    if (oldPriceRes.success && oldPriceRes.data) {
                        row.oldPrice = oldPriceRes.data.oldPrice ?? 0;
                    } else {
                        row.oldPrice = 0;
                    }
                } catch (err) {
                    row.oldPrice = 0;
                }
            }

            setRows(rows); // 所有 oldPrice 都获取完毕后再更新状态

        } catch (error) {
            // 表单验证失败或其他错误
            console.error("onFinish 错误:", error);
            antMessage('error', '请求失败', '未知错误');
        } finally {
            setIsLoading(false);
        }
    };

    // ================= ✅ 核心修改：打印配置（白底黑字+自动缩放） =================
    const handlePrint = useReactToPrint({
        contentRef,
        documentTitle: '报价对比表',


    });

    return (
        <div>
            {/* ✅ 非打印区域，打印时自动隐藏 */}
            <div className="no-print">
                <Form form={form} onFinish={onFinish}>
                    <Row gutter={24}>
                        <Col span={6}>
                            <Form.Item name="submissionTime" label="日期范围">
                                <RangePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="quotationNoticeId" label="公告">
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="公告"
                                    optionFilterProp="children"
                                    filterOption={(input, option: any) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                                    {getList.generalEx(quotationNotice, { itemName: "quotationNoticeName", includeNull: true })}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="queryString" label="查询">
                                <Input placeholder="输入产品名称" />
                            </Form.Item>
                        </Col>

                        <Col span={4}>
                            <Form.Item>
                                <Space>
                                    <Button type="primary" loading={isLoading} htmlType="submit">统计</Button>
                                    <Button onClick={handlePrint} type="default">打印</Button>
                                    <Button onClick={() => DownloadExcel(rows, "quotationComparisonTwo", { date: form.getFieldValue("submissionTime"), columnsHead: printColumns })}>下载EXCEL文件</Button>
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </div>

            {/* ✅ 打印容器 */}
            <div ref={contentRef} style={{ marginTop: 20 }}>
                {/* 屏幕显示的表格，打印时隐藏 */}
                <div className="screen-table" style={{ overflowX: 'auto' }}>
                    <Table<TableRowData>
                        rowKey="key"
                        columns={screenColumns}
                        dataSource={rows}
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                        bordered
                        size="small"
                        loading={isLoading}
                        onChange={handleTableChange}
                    />
                </div>

                {/* ✅ 打印专用表格：白底黑字+自动缩放 */}
                <div
                    className="print-table"
                    style={{
                        display: 'none',
                        transform: `scale(${printScale})`,
                        transformOrigin: 'top left',
                        width: printScale < 1 ? `${100 / printScale}%` : '100%'
                    }}
                >
                    <Table<TableRowData>
                        rowKey="key"
                        columns={printColumns}
                        dataSource={rows}
                        pagination={false}
                        bordered
                        size="small"
                        loading={isLoading}
                        onChange={handleTableChange}
                    />
                </div>
            </div>

            {/* ✅ 注入全局打印样式：强制白底黑字，无任何报错风险 */}
            <style dangerouslySetInnerHTML={{
                __html: `
                    @media print {
                        /* 1. 强制A4横向，无边距 */
                        @page {
                            size: A4 landscape;
                            margin: 0;
                        }

                        /* 2. 隐藏非打印元素 */
                        .no-print, .screen-table {
                            display: none !important;
                        }

                        /* 3. 显示打印表格 */
                        .print-table {
                            display: block !important;
                        }

                        /* 4. ✅ 核心：强制白底黑字 */
                        .print-table table {
                            border-collapse: collapse !important;
                            width: 100% !important;
                            table-layout: auto !important; /* 自动分配列宽 */
                        }

                        /* 表头：白底黑字加粗 */
                        .print-table .ant-table-thead > tr > th {
                            background: #ffffff !important; /* 强制白底 */
                            color: #000000 !important; /* 强制黑字 */
                            border: 1px solid #000000 !important;
                            font-weight: bold !important;
                            font-size: 12px !important;
                            padding: 6px 8px !important;
                        }

                        /* 单元格：白底黑字 */
                        .print-table .ant-table-tbody > tr > td {
                            background: #ffffff !important; /* 强制白底 */
                            color: #000000 !important; /* 强制黑字 */
                            border: 1px solid #000000 !important;
                            font-size: 11px !important;
                            padding: 4px 8px !important;
                            white-space: nowrap !important; /* 防止文字换行撑大表格 */
                        }

                        /* 5. 防止内容跨页截断 */
                        .print-table tr {
                            page-break-inside: avoid !important;
                        }

                        /* 6. 强制打印背景色（解决浏览器省墨模式问题） */
                        body {
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }

                        /* 7. 清除Antd残留样式 */
                        .print-table .ant-table-cell-fix-left,
                        .print-table .ant-table-cell-fix-right {
                            position: static !important;
                        }
                        .print-table .ant-table-container,
                        .print-table .ant-table-content,
                        .print-table .ant-table-body {
                            overflow: visible !important;
                            max-height: none !important;
                        }
                    }
                `
            }} />
        </div>
    );
}